import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api } from "../lib/api";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
  phone?: string;
  timezone?: string;
  language?: string;
  twoFactorEnabled?: boolean;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  website?: string;
  industry?: string;
  description?: string;
  logo?: string;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; name: string; orgName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  updateOrganization: (data: Partial<Organization>) => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    try {
      const data = await api.get<{ user: User; organization: Organization }>("/auth/me");
      setUser(data.user);
      setOrganization(data.organization);
    } catch {
      // Not authenticated
    }
  }, []);

  useEffect(() => {
    refreshAuth().finally(() => setLoading(false));
  }, [refreshAuth]);

  const login = async (email: string, password: string) => {
    const data = await api.post<{ user: User; organization: Organization }>("/auth/login", { email, password });
    setUser(data.user);
    setOrganization(data.organization);
  };

  const register = async (body: { email: string; password: string; name: string; orgName?: string }) => {
    const data = await api.post<{ user: User; organization: Organization }>("/auth/register", body);
    setUser(data.user);
    setOrganization(data.organization);
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setOrganization(null);
  };

  const updateUser = useCallback((data: Partial<User>) => {
    setUser((prev) => prev ? { ...prev, ...data } : null);
  }, []);

  const updateOrganization = useCallback((data: Partial<Organization>) => {
    setOrganization((prev) => prev ? { ...prev, ...data } : null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, organization, loading, login, register, logout, updateUser, updateOrganization, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
